import { isOrgAdmin } from './instructorPortalAccess';
import {
  INVITE_TEAM_MIN_TIER,
  canInviteTeamMembers,
  inviteTeamMembersGateBody,
} from './instructorMeterLimits';

/**
 * True when Admin → Users may load the org-scoped member list (never platform-wide).
 * Super admins must view-as an org admin; org admins see only their orgId.
 */
export function canListOrgMembers(userType, entitlements) {
  // Requires a concrete org-admin context (role=admin + a resolved orgId). A super
  // admin who is also an admin of this org may view its members directly — their
  // entitlements.orgId gives the concrete org, so no extra "must impersonate" block.
  if (!isOrgAdmin(entitlements) || !entitlements?.orgId) {
    return false;
  }
  return true;
}

/**
 * Resolves Admin → Users action eligibility from org role + plan entitlements.
 * Create, invite, and import all require org admin + Organization-tier team management.
 */
export function resolveAdminUsersGuards(entitlements) {
  const isAdmin = isOrgAdmin(entitlements);
  const canManageTeam = isAdmin && canInviteTeamMembers(entitlements);
  const tier = entitlements?.tier || 'starter';
  const minTierLabel = INVITE_TEAM_MIN_TIER.charAt(0).toUpperCase() + INVITE_TEAM_MIN_TIER.slice(1);

  return {
    isAdmin,
    canManageTeam,
    canCreateUser: canManageTeam,
    canInvite: canManageTeam,
    canImportUsers: canManageTeam,
    tier,
    teamGateBody: inviteTeamMembersGateBody(entitlements),
    roleBlockedMessage: 'Only organization admins can manage team members.',
    planBlockedMessage: `Team management requires the ${minTierLabel} plan or higher. You are on ${tier}.`,
    importBlockedMessage: `Bulk user import requires the ${minTierLabel} plan or higher.`,
    selfBlockedMessage: 'You cannot change your own access from this list.',
  };
}

/** True when the row is the signed-in (or impersonated) viewer. */
export function isSelfOrgMember(viewerUserId, row) {
  if (!viewerUserId || !row) {
    return false;
  }
  const targetId = row.userId || row.studentId;
  return Boolean(targetId && targetId === viewerUserId);
}
